import {
  Layout,
  Badge,
  Dropdown,
  Avatar,
  Space,
  Button,
  Breadcrumb,
} from "antd";
import {
  BellOutlined,
  GlobalOutlined,
  CalendarOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  HomeOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_notification } from "utils/api";

const { Header: AntHeader } = Layout;

// ==============================|| HEADER ||============================== //

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // State for user details and notifications
  const [userDetails, setUserDetails] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  // Calendar is purely a ticket view, so its icon should follow Tickets
  // read access — users without Tickets shouldn't see (or be able to
  // navigate to) the calendar from the header.
  const [hasTicketsAccess, setHasTicketsAccess] = useState(false);

  // Load user details from localStorage on mount
  useEffect(() => {
    const storedUserDetails = localStorage.getItem("user_details");
    if (storedUserDetails) {
      try {
        setUserDetails(JSON.parse(storedUserDetails));
      } catch (error) {
        console.error("Error parsing user details:", error);
      }
    }
    try {
      const raw = localStorage.getItem("access_list_data");
      if (raw && raw !== "undefined" && raw !== "null") {
        const list = JSON.parse(raw);
        if (Array.isArray(list)) {
          setHasTicketsAccess(
            Boolean(
              list.find(
                (item) => item.module_name === "Tickets" && item.read_access,
              ),
            ),
          );
        }
      }
    } catch {
      setHasTicketsAccess(false);
    }
  }, []);

  // Fetch notification count — only when the user has Tickets access.
  // Notifications are entirely ticket-driven (assigned, status changes,
  // mentions), so polling for users without Tickets access is wasted
  // network and would surface counts the user can't act on.
  useEffect(() => {
    if (!hasTicketsAccess) {
      setNotificationCount(0);
      return undefined;
    }
    const fetchNotificationCount = () => {
      // Use the same "all" scope the Notifications page uses so the badge count
      // matches the number of notification cards. The "count" scope caps the
      // backend result at 5, which made the badge under-report (e.g. badge 5 vs
      // 10/15 actual notifications).
      get_notification(
        (res) => {
          if (res?.status === "success" && res?.data) {
            setNotificationCount(res.data.length);
          }
        },
        { count: "all" },
      );
    };

    fetchNotificationCount();
    // Refresh notification count every 30 seconds
    const interval = setInterval(fetchNotificationCount, 30000);

    return () => clearInterval(interval);
  }, [hasTicketsAccess]);

  // Get display name from user details
  const getDisplayName = () => {
    if (!userDetails) return t("User");

    const firstName = userDetails.first_name || "";
    const lastName = userDetails.last_name || "";

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (userDetails.username) {
      return userDetails.username;
    }

    return "User";
  };

  // Generate breadcrumb items from current path
  const getBreadcrumbItems = () => {
    const pathSnippets = location.pathname.split("/").filter((i) => i);

    const items = [
      {
        title: (
          <Link to="/">
            <HomeOutlined />
          </Link>
        ),
      },
    ];

    pathSnippets.forEach((snippet, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
      const rawTitle = snippet
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .split(/[-_]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      const title = t(rawTitle, { defaultValue: rawTitle });

      items.push({
        title:
          index === pathSnippets.length - 1 ? (
            title
          ) : (
            <Link to={url}>{title}</Link>
          ),
      });
    });

    return items;
  };

  // User dropdown menu items
  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: t("Profile"),
      onClick: () => navigate("/user-profile"),
    },
    // {
    //   key: "settings",
    //   icon: <SettingOutlined />,
    //   label: "Settings",
    //   onClick: () => navigate("/company-profile"),
    // },
    // {
    //   type: "divider",
    // },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: t("Logout"),
      onClick: () => {
        localStorage.clear();
        navigate("/login");
      },
    },
  ];

  // Change app language and persist for next load
  const handleLanguageChange = (lng) => {
    if (lng === i18n.language) return;
    localStorage.setItem("lang", lng);
    i18n.changeLanguage(lng);
    window.location.reload();
  };

  // Language dropdown menu items
  const currentLang = (i18n.language || "en").split("-")[0];
  const languages = [
    { key: "en", label: "English" },
    { key: "hi", label: "हिंदी" },
    { key: "mr", label: "मराठी" },
    // { key: "kn", label: ("ಕನ್ನಡ") },
    { key: "th", label: "ภาษาไทย" },
  ];
  const languageMenuItems = languages.map(({ key, label }) => ({
    key,
    label,
    onClick: () => handleLanguageChange(key),
    extra:
      currentLang === key ? (
        <CheckOutlined className="text-primary-600" />
      ) : null,
  }));

  return (
    <AntHeader
      className="bg-white px-4 flex items-center justify-between sticky top-0 shadow-sm border-b border-gray-100 h-16"
      style={{ zIndex: 1000 }}
    >
      {/* Left Section - Breadcrumbs */}
      <div className="flex items-center">
        <Breadcrumb items={getBreadcrumbItems()} className="flex" />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Language Selector */}
        <Dropdown
          menu={{
            items: languageMenuItems,
            selectable: true,
            selectedKeys: [currentLang],
          }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ zIndex: 1050 }}
        >
          <Button
            type="text"
            icon={<GlobalOutlined />}
            className="hover:bg-gray-100"
          />
        </Dropdown>

        {/* Calendar — hidden when the user has no Tickets access since
            the calendar only shows ticket data. */}
        {hasTicketsAccess && (
          <Button
            type="text"
            icon={<CalendarOutlined />}
            className="hover:bg-gray-100"
            onClick={() => navigate("/calendar")}
          />
        )}

        {/* Notifications — hidden when the user has no Tickets access
            since every notification kind is ticket-driven. */}
        {hasTicketsAccess && (
          <Badge count={notificationCount} size="small">
            <Button
              type="text"
              icon={<BellOutlined />}
              className="hover:bg-gray-100"
              onClick={() => navigate("/notifications")}
            />
          </Badge>
        )}

        {/* User Profile */}
        <Dropdown
          menu={{ items: userMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{ zIndex: 1050 }}
        >
          <Space className="cursor-pointer hover:bg-gray-100 rounded-lg ml-2 h-10 p-3">
            <Avatar
              size={24}
              icon={<UserOutlined />}
              className="bg-primary-600"
            />
            <span className="hidden md:inline text-sm font-medium">
              {getDisplayName()}
            </span>
          </Space>
        </Dropdown>
      </div>
    </AntHeader>
  );
};

export default Header;
